/**
 * Utility functions for HumioIntegrationProvider
 */
import { ApolloError } from '@apollo/client';

import { isValidToken } from '../shared/utils';
import { TOKEN_CONFIG } from './HumioIntegrationProvider.const';
import {
  TokenCacheEntry,
  WindowWithDebug,
} from './HumioIntegrationProvider.types';

/**
 * Token cache for performance optimization
 * Key format: "{userId}:{humioUrlPrefix}"
 */
export const tokenCache = new Map<string, TokenCacheEntry>();

/**
 * Rate limiting tracker for refresh attempts
 * Key format: "{userId}:{humioUrlPrefix}"
 */
export const refreshAttempts = new Map<string, number[]>();

/**
 * Environment detection and logging configuration
 */
export const hasWindow = typeof window !== 'undefined';
export const hasStorage = hasWindow && !!window.localStorage;

// Enable debug logging only in development/staging environments
const isProduction = process.env.NODE_ENV === 'production';
const DEBUG =
  !isProduction && hasWindow && !!(window as WindowWithDebug).__HUMIO_DEBUG__;

export const warn = DEBUG
  ? (...args: unknown[]) => console.warn('[HumioIntegration]', ...args)
  : () => {
      void 0;
    };

export const error = DEBUG
  ? (...args: unknown[]) => console.error('[HumioIntegration]', ...args)
  : () => {
      void 0;
    };

/**
 * Check if rate limit has been exceeded for token refresh
 */
export const checkRateLimit = (cacheKey: string): boolean => {
  const now = Date.now();
  const attempts = refreshAttempts.get(cacheKey) ?? [];
  const recentAttempts = attempts.filter(
    (ts) => now - ts < TOKEN_CONFIG.RATE_LIMIT_WINDOW_MS,
  );

  return recentAttempts.length >= TOKEN_CONFIG.MAX_REFRESH_ATTEMPTS;
};

/**
 * Record a refresh attempt for rate limiting
 */
export const recordRefreshAttempt = (cacheKey: string): void => {
  const now = Date.now();
  const attempts = refreshAttempts.get(cacheKey) ?? [];
  const recentAttempts = attempts.filter(
    (ts) => now - ts < TOKEN_CONFIG.RATE_LIMIT_WINDOW_MS,
  );

  refreshAttempts.set(cacheKey, [...recentAttempts, now]);
};

/**
 * Get cached token if valid and not expired
 */
export const getCachedToken = (cacheKey: string): string | undefined => {
  const now = Date.now();
  const cached = tokenCache.get(cacheKey);

  if (
    cached &&
    now - cached.timestamp < TOKEN_CONFIG.TOKEN_CACHE_DURATION_MS &&
    isValidToken(cached.token)
  ) {
    return cached.token;
  }

  return undefined;
};

/**
 * Update token cache
 */
export const updateTokenCache = (
  cacheKey: string,
  token: string,
  timestamp: number,
): void => {
  tokenCache.set(cacheKey, {
    token,
    createdAt: timestamp,
    timestamp,
  });
};

/**
 * Clear token from cache
 */
export const clearTokenFromCache = (cacheKey: string): void => {
  tokenCache.delete(cacheKey);
  refreshAttempts.delete(cacheKey);
};

/**
 * Clear all caches
 */
export const clearAllCaches = (): void => {
  tokenCache.clear();
  refreshAttempts.clear();
};

/**
 * Check if token needs refresh based on age
 */
export const needsTokenRefresh = (
  token: string | undefined,
  tokenCreatedAt: number,
  forceRefresh: boolean,
): boolean => {
  const now = Date.now();
  const tokenAge = tokenCreatedAt ? now - tokenCreatedAt : Infinity;

  return (
    forceRefresh ||
    tokenAge >= TOKEN_CONFIG.PROACTIVE_REFRESH_MS ||
    !token ||
    !isValidToken(token)
  );
};

/**
 * Check if refresh should be throttled
 */
export const shouldThrottleRefresh = (lastRefreshAt: number): boolean => {
  const now = Date.now();
  return !!(
    lastRefreshAt && now - lastRefreshAt < TOKEN_CONFIG.MIN_REFRESH_INTERVAL_MS
  );
};

/**
 * Check if dashboard query error is authentication related
 */
export const isAuthenticationError = (error: ApolloError): boolean => {
  const codes =
    error?.graphQLErrors?.map((g) => g.extensions?.code || '') || [];
  const msg = error?.message?.toLowerCase?.() || '';

  return (
    codes.includes('UNAUTHORIZED') ||
    codes.includes('FORBIDDEN') ||
    msg.includes('unauthorized') ||
    msg.includes('forbidden') ||
    msg.includes('invalid') ||
    msg.includes('session') ||
    msg.includes('internal server error')
  );
};

/**
 * Check if error is downstream service error
 */
export const isDownstreamError = (error: ApolloError): boolean => {
  const codes =
    error?.graphQLErrors?.map((g) => g.extensions?.code || '') || [];
  return codes.includes('DOWNSTREAM_SERVICE_ERROR');
};

/**
 * Calculate retry delay with exponential backoff
 */
export const calculateRetryDelay = (
  retryCount: number,
  isDownstream: boolean,
): number => {
  const baseDelay = isDownstream
    ? TOKEN_CONFIG.DOWNSTREAM_RETRY_DELAY_MS
    : TOKEN_CONFIG.DASHBOARD_RETRY_DELAY_MS;

  const exponent = isDownstream ? Math.min(retryCount, 3) : retryCount - 1;
  return baseDelay * Math.pow(2, exponent);
};
